import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';
import styled from 'styled-components';
import {
    Typography,
    Paragraph,
} from '@ratehub/base-ui';

import QuoteDetails from './QuoteDetails';


function QuoteDetailsHome({ coverage, endorsements, brokerName, brokerPhone, ...otherProps }) {
    const intl = useIntl();

    const coverageDetails = buildCoverageDetailsShape(coverage, intl);
    const endorsementDetails = buildEndorsementsShape(endorsements, intl);

    return (
        <>
            <QuoteDetails
                coverageDetails={coverageDetails}
                endorsementDetails={endorsementDetails}
                {...otherProps}
            />
            <QuoteDetailsHome.ContactInfo size="medium">
                {intl.formatMessage(Messages.SUPPORT, {
                    broker: brokerName,
                    contact: brokerPhone,
                })}
            </QuoteDetailsHome.ContactInfo>
        </>
    );
}

QuoteDetailsHome.propTypes = {
    coverage: PropTypes.shape({
        policyLength: PropTypes.number.isRequired,
        personalLiability: PropTypes.number.isRequired,
        claimsDeductible: PropTypes.number.isRequired,
    }).isRequired,
    endorsements: PropTypes.shape({
        sewerBackup: PropTypes.bool.isRequired,
        overlandWater: PropTypes.bool.isRequired,
    }).isRequired,
    brokerName: PropTypes.string.isRequired,
    brokerPhone: PropTypes.string.isRequired,
};

/* This function formats our coverage object into the shape needed
    for QuoteDetails since that component is re-used between Home + Auto.
    Additionally, formatting strings needs to be done here since some items
    use formatNumber & have unique messages.  */
function buildCoverageDetailsShape(coverage, intl) {
    return [
        {
            label: intl.formatMessage(Messages.POLICY_LENGTH),
            value: intl.formatMessage(Messages.MONTHS, { amount: coverage.policyLength }),
        },
        {
            label: intl.formatMessage(Messages.PERSONAL_LIABILITY),
            value: intl.formatNumber(coverage.personalLiability, {
                style: 'currency',
                currency: 'CAD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }),
        },
        {
            label: intl.formatMessage(Messages.CLAIMS_DEDUCTIBLE),
            value: intl.formatNumber(coverage.claimsDeductible, {
                style: 'currency',
                currency: 'CAD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }),
        },
    ];
}

/* This function formats our endorsements object into the shape needed
    for QuoteDetails since that component is re-used between Home + Auto.
    Additionally, formatting strings needs to be done here since some items
    use formatNumber & have unique messages.  */
function buildEndorsementsShape(endorsements, intl) {
    return [
        {
            label: intl.formatMessage(Messages.SEWER_BACKUP),
            value: endorsements.sewerBackup,
        },
        {
            label: intl.formatMessage(Messages.OVERLAND_WATER),
            value: endorsements.overlandWater,
        },
    ];
}

const Messages = defineMessages({
    POLICY_LENGTH: {
        id: 'Accounts.homeQuoteDetails.policyLength',
        defaultMessage: 'Policy length',
    },
    MONTHS: {
        id: 'Accounts.homeQuoteDetails.months',
        defaultMessage: '{amount} months',
    },
    PERSONAL_LIABILITY: {
        id: 'Accounts.homeQuoteDetails.personalLiability',
        defaultMessage: 'Personal liability',
    },
    CLAIMS_DEDUCTIBLE: {
        id: 'Accounts.homeQuoteDetails.claimsDeductible',
        defaultMessage: 'Claims deductible',
    },
    SEWER_BACKUP: {
        id: 'Accounts.homeQuoteDetails.sewerBackup',
        defaultMessage: 'Sewer back-up',
    },
    OVERLAND_WATER: {
        id: 'Accounts.homeQuoteDetails.overlandWater',
        defaultMessage: 'Overland water',
    },
    IDENTITY_THEFT: {
        id: 'Accounts.homeQuoteDetails.identityTheft',
        defaultMessage: 'Identity theft',
    },
    SUPPORT: {
        id: 'Accounts.homeQuoteDetails.brokerSupport',
        defaultMessage: '{broker} support: {contact}',
    },
});
QuoteDetailsHome.ContactInfo = styled(Paragraph)`
    font-weight: ${Typography.WEIGHTS.MEDIUM};
`;

export default QuoteDetailsHome;
